<template>
  <div>
    <div class="alert alert-primary mb-0 mt-2 mx-2 d-flex align-items-center">
      <feather-icon
        icon="BellIcon"
        class="mr-50 mt-n25"
        size="18"
      />

      Menampilkan versi lama dari halaman tabel produk eksternal

      <button
        class="btn btn-primary ml-auto"
        @click="switchToNew"
      >
        Buka versi baru
      </button>
    </div>

    <table-header
      search
      search-placeholder="Cari berdasarkan nama"
      search-action="products/getProductExternal"
      :actions="['filter']"
    >
      <template #filter>
        <v-select
          v-model="statusFilter"
          :searchable="false"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="statusOptions"
          :reduce="val => val.key"
          @input="handleChangeStatus"
        />
      </template>
    </table-header>

    <b-skeleton-table
      v-if="isLoading"
      :rows="5"
      :columns="5"
    />
    <b-table
      v-else
      ref="refInvoiceListTable"
      :items="externalProducts.data"
      responsive
      :fields="tableFields"
      primary-key="id"
      show-empty
      empty-text="No matching records found"
      class="position-relative mh-160"
    >

      <!-- Column: Product -->
      <template #cell(product)="data">
        <b-media vertical-align="center">
          <template #aside>
            <b-img
              rounded
              width="50"
              :src="linkStaging(`${data.item.product.images.length ? data.item.product.images[0].image_url : ''}`, 50, 50)"
              :alt="data.item.product.name"
              @error="(event) => {
                event.target.src = require('@/assets/images/icons/broken-image.svg')
              }"
            />
          </template>
          <span class="font-weight-bold d-block">
            {{ data.item.product.name }}
          </span>
          <small class="text-muted mr-50">by {{ data.item.owner_gift.owner.name }}</small>

          <small
            v-if="data.item.owner_gift.owner.dont_switch_external_registry"
            class="badge badge-danger"
          >
            <feather-icon
              icon="FlagIcon"
              class="mr-25"
            />
            Ditandai Jangan Ubah</small>
        </b-media>
      </template>

      <!-- Column: Status -->
      <template #cell(url)="data">
        <div
          class="card-text text-truncate"
          style="width: 300px"
        >
          <a
            v-b-tooltip.hover.top="'Buka URL di tab baru'"
            :href="data.item.product.url"
            target="_blank"
          >{{ data.item.product.url }}</a>
        </div>
      </template>

      <!-- Column: URL -->
      <template #cell(status)="data">
        <b-badge
          v-if="data.item.is_purchase"
          variant="light-success"
        >
          Terbeli
        </b-badge>
        <b-badge
          v-else
          variant="light-secondary"
        >
          Belum Terbeli
        </b-badge>
      </template>

      <!-- Column: Price -->
      <template #cell(price)="data">
        <span class="text-nowrap">
          {{ toCurrency(data.item.product.price) }}
        </span>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <table-action
            :actions="tableAction"
            :data="data"
          />
        </div>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <pagination
        :data="externalProducts.meta"
        action="products/getProductExternal"
      />
    </div>

    <!-- Detail Produk External -->
    <b-modal
      v-model="isShowDetailProductExternal"
      title="Detail Produk External"
      hide-footer
    >
      <div v-if="detailProductExternal">
        <product-external-detail :data="detailProductExternal" />
      </div>
    </b-modal>

    <!-- Edit Url Produk External -->
    <b-modal
      v-if="canAccess('manage', 'Product')"
      v-model="isEditUrl"
      title="Edit Url Produk External"
      hide-footer
      centered
    >
      <div v-if="detailProductExternal">
        <product-external-edit-url
          :data="detailProductExternal"
          @finish="onFinishEditUrl"
        />
      </div>
    </b-modal>

    <!-- Ubah produk eksternal ke produk internal -->
    <b-modal
      v-if="canAccess('manage', 'Product')"
      v-model="isShowSwitch"
      title="Ubah produk eksternal ke produk internal"
      hide-footer
      no-close-on-backdrop
    >
      <div v-if="detailProductExternal">
        <product-external-switch
          :data="detailProductExternal"
          @finish="onFinishSwitch"
        />
      </div>
    </b-modal>

    <!-- Tandai jangan ubah produk eksternal ke produk internal -->
    <b-modal
      v-if="canAccess('manage', 'Product')"
      v-model="isShowDoNotSwitchToInternal"
      :title="detailProductExternal && detailProductExternal.owner_gift && detailProductExternal.owner_gift.owner.dont_switch_external_registry
        ? 'Tandai boleh ubah'
        : 'Tandai jangan ubah'"
      ok-title="Simpan"
      cancel-title="Batal"
      cancel-variant="white"
      no-close-on-backdrop
      :ok-disabled="$store.state.user.isLoading"
      @ok.prevent="submitDoNotChangeToInternal"
      @cancel="isShowDoNotSwitchToInternal = false"
      @hidden="isShowDoNotSwitchToInternal = false"
    >
      <div
        v-if="detailProductExternal && detailProductExternal.owner_gift && detailProductExternal.owner_gift.owner"
      >Tandai {{ detailProductExternal && detailProductExternal.owner_gift && detailProductExternal.owner_gift.owner.dont_switch_external_registry ? 'boleh' : 'jangan ' }} ubah semua produk eksternal milik <b>{{ detailProductExternal.owner_gift.owner.name }}</b> ke produk internal.</div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  BTable,
  BMedia,
  BImg,
  BSkeletonTable,
  BBadge,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import TableAction from '@/layouts/components/custom/TableAction.vue'
import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ProductExternalDetail from '../custom/ProductExternalDetail.vue'
import ProductExternalEditUrl from '../custom/ProductExternalEditUrl.vue'
import ProductExternalSwitch from '../custom/ProductExternalSwitch.vue'

export default {
  components: {
    BSkeletonTable,
    BTable,
    BMedia,
    BImg,
    BBadge,
    TableAction,
    Pagination,
    ProductExternalDetail,
    ProductExternalEditUrl,
    ProductExternalSwitch,
    TableHeader,
    vSelect,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tableFields: [
        { key: 'product', label: 'Produk' },
        { key: 'brand', label: 'Brand', thStyle: { width: '10%' } },
        { key: 'status', label: 'Status' },
        { key: 'url', label: 'Url' },
        { key: 'price', label: 'Harga' },
        { key: 'actions', label: '', thStyle: { width: '1%' } },
      ],
      detailProductExternal: '',
      isShowDetailProductExternal: false,
      isEditUrl: false,
      isShowSwitch: false,
      isShowDoNotSwitchToInternal: false,
      required,
      statusFilter: null,
    }
  },
  computed: {
    ...mapState('products', ['externalProducts', 'isLoading', 'statusOptions']),
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: null,
          to: null,
          action: data => { this.onShowDetailProductExternal(data) },
          icon: 'EyeIcon',
          title: 'Detail',
        },
        {
          access: this.canAccess('manage', 'Product'),
          to: null,
          action: data => { this.onEditProductExternal(data) },
          icon: 'EditIcon',
          title: 'Edit Url',
        },
        {
          access: this.canAccess('manage', 'Product'),
          to: null,
          action: data => { this.onSwitchProductExternal(data) },
          icon: 'RefreshCcwIcon',
          title: 'Ubah ke Internal',
        },
        {
          access: this.canAccess('manage', 'Product'),
          to: null,
          action: data => { this.onDoNotChangeToInternal(data) },
          icon: 'FlagIcon',
          title: 'Tandai jangan ubah',
        },
      ]
    },
  },
  async created() {
    // filter init
    this.statusFilter = await this.query.status ? this.query.status : null
    await this.getProductExternal({ ...this.query })

    // if (!this.externalProducts.data.length) {
    //   await this.getProductExternal({ ...this.query })
    // }
  },
  methods: {
    ...mapActions('products', ['getProductExternal']),
    switchToNew() {
      localStorage.setItem('externalProductPageVersion', 'new')
      window.location.reload()
    },
    onShowDetailProductExternal(data) {
      this.detailProductExternal = data
      this.isShowDetailProductExternal = true
    },
    onEditProductExternal(data) {
      this.detailProductExternal = data
      this.isEditUrl = true
    },
    async onSwitchProductExternal(data) {
      await this.$store.commit('products/RESET_SWITCH')
      await this.$store.commit('products/RESET_PRODUCT_SEARCH')
      this.detailProductExternal = data
      this.$store.commit('products/SET_OPEN_SWITCH', true)
      this.isShowSwitch = true
    },
    async onDoNotChangeToInternal(data) {
      this.detailProductExternal = data
      this.$store.commit('products/SET_OPEN_DO_NOT_SWITCH', true)
      this.isShowDoNotSwitchToInternal = true
    },
    async submitDoNotChangeToInternal() {
      if (!this.detailProductExternal || !this.detailProductExternal.owner_gift || !this.detailProductExternal.owner_gift.owner) {
        return
      }

      const newStatus = !this.detailProductExternal.owner_gift.owner.dont_switch_external_registry

      this.$store.dispatch('user/doNotChangeToInternal', {
        id: this.detailProductExternal.owner_gift.owner.id,
        dontSwitchExternalRegistry: newStatus,
      })
        .then(res => {
          if (res) {
            this.$store.commit('products/SET_PRODUCT_EXTERNAL', {
              ...this.externalProducts,
              data: this.externalProducts.data.map(item => ({
                ...item,
                owner_gift: {
                  ...item.owner_gift,
                  owner: {
                    ...item.owner_gift.owner,
                    dont_switch_external_registry: item.owner_gift.owner.id === this.detailProductExternal.owner_gift.owner.id
                      ? newStatus
                      : item.owner_gift.owner.dont_switch_external_registry,
                  },
                },
              })),
            })

            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Berhasil',
                text: 'Berhasil ditandai jangan ubah ke internal!',
                icon: 'BellIcon',
                variant: 'success',
              },
            })

            this.isShowDoNotSwitchToInternal = false
            this.detailProductExternal = null
          }
        })
    },
    async onFinishEditUrl() {
      await this.getProductExternal({ ...this.query })
    },
    async onFinishSwitch() {
      this.$store.commit('products/SET_OPEN_SWITCH', false)
      await this.$store.commit('products/RESET_SWITCH')
      this.isShowSwitch = false
      await this.getProductExternal({ ...this.query })
    },
    async handleChangeStatus(data) {
      if (data !== null) {
        this.$router.push({ query: { tab: 3, status: data } })
        await this.getProductExternal({
          page: this.page,
          status: data,
        })
      } else {
        this.$router.push({ query: { ...this.query, page: 1, status: '' } })
        await this.getProductExternal({ page: 1 })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
</style>
